import * as React from 'react'
import { useMemo } from 'react'
import { Button, makeStyles, Popover, PopoverSurface, PopoverTrigger, Text, tokens } from '@fluentui/react-components'
import { NormalizedError } from '../../reducers/errorReducer'
import { bundleIcon, InfoFilled, InfoRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'

const InfoIcon = bundleIcon(InfoFilled, InfoRegular)

const useStyles = makeStyles({
    surface: {
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        gap: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
        alignItems: 'baseline',
    },
    details: {
        overflow: 'auto',
        maxHeight: '10em',
    },
})

export default function ErrorPopover(props: { error?: Pick<NormalizedError, 'message' | 'trace'> }) {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const parsedDetails = useMemo(() => {
        if (props.error?.trace?.includes('templateNotFound')) {
            return translate('error.templatenotfound')
        }
        // Marker set by GenerationExpiredException in the document service: the generation gave up on itself
        // before the Functions host could time out over it.
        if (props.error?.trace?.includes('generationExpired')) {
            return translate('error.generationexpired')
        }
        if (props.error?.trace?.indexOf('nameAlreadyExists') !== -1) {
            return translate('error.namealreadyexists')
        }
        return props.error?.trace
    }, [props.error])

    return (
        <>
            {props.error?.message && (
                <Popover withArrow>
                    <PopoverTrigger disableButtonEnhancement>
                        <Button icon={<InfoIcon />} appearance="subtle">
                            {translate('toast.error.open')}
                        </Button>
                    </PopoverTrigger>
                    <PopoverSurface className={styles.surface}>
                        <Text size={200}>{translate('toast.error.label')}</Text>
                        <Text className={styles.details}>{props.error.message}</Text>
                        {props.error.trace && <Text size={200}>Details</Text>}
                        {props.error.trace && <Text className={styles.details}>{parsedDetails}</Text>}
                    </PopoverSurface>
                </Popover>
            )}
        </>
    )
}
